import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Folder, FolderPlus, FileText, HardHat, BarChart3, ShieldCheck, CalendarRange, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CreateAssessmentDialog from "../../CreateAssessmentDialog";
import CreateFolderDialog from "../../CreateFolderDialog";
import { CardActionItem } from "./CardActionItem";
import { ActionGroup } from "./ActionGroup";
import type { Database } from "@/integrations/supabase/types";


type FolderType = Database['public']['Tables']['folders']['Row'];

interface ModernEmptyStateProps {
  folderData: FolderType | null;
  projectId: string;
  folderId: string;
  isShared: boolean;
  onStartAssessment?: (projectId: string, folderId: string) => void;
  onCreateFolder: (name: string, description?: string) => Promise<void>;
  /** Resolves a folder to place new content in when standing at project level */
  ensureFolderId?: () => Promise<string | null>;
}

export const ModernEmptyState: React.FC<ModernEmptyStateProps> = ({
  folderData,
  projectId,
  folderId,
  isShared,
  onStartAssessment,
  onCreateFolder,
  ensureFolderId,
}) => {
  const navigate = useNavigate();
  const [isCreateAssessmentDialogOpen, setIsCreateAssessmentDialogOpen] = useState(false);
  const [isCreateFolderDialogOpen, setIsCreateFolderDialogOpen] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [activeFolderId, setActiveFolderId] = useState<string>(folderId);

  const canCreateContent = Boolean(folderId) || Boolean(ensureFolderId);

  const resolveFolderId = async (): Promise<string | null> => {
    if (folderId) return folderId;
    if (!ensureFolderId) return null;
    return await ensureFolderId();
  };

  const handleCreateAssessmentSuccess = () => {
    setIsCreateAssessmentDialogOpen(false);
  };

  const handleCreateFolderSubmit = async () => {
    if (!folderName.trim()) return;
    await onCreateFolder(folderName.trim());
    setFolderName("");
    setIsCreateFolderDialogOpen(false);
  };

  const handleCreateRiskAssessment = async () => {
    const target = await resolveFolderId();
    if (!target) return;
    setActiveFolderId(target);
    setIsCreateAssessmentDialogOpen(true);
  };

  const handleCreateSKRAssessment = async () => {
    const target = await resolveFolderId();
    if (!target) return;
    navigate(`/skr-bedomning/${projectId}/${target}?createNew=true`);
  };

  const handleCreateWorkPreparation = async () => {
    const target = await resolveFolderId();
    if (!target) return;
    navigate(`/arbetsberedning/${projectId}/${target}`);
  };

  const handleCreateWorkArea = () => {
    navigate(`/arbetsomrade/${projectId}`);
  };

  const openSamordning = (flik: string) => {
    navigate(`/samordning/${projectId}?flik=${flik}`);
  };


  return (
    <>
      <div className="flex items-center justify-center min-h-[500px] px-4 py-8">
        <Card className="max-w-4xl w-full overflow-hidden border border-border/70 bg-card shadow-lg">
          <div className="h-px w-full bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
          <CardContent className="relative p-8 sm:p-12 space-y-10">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-primary/[0.05] to-transparent" />

            {/* Header */}
            <div className="relative text-center space-y-5">
              <div className="flex justify-center">
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 ring-1 ring-inset ring-primary/20 shadow-sm">
                  <Folder className="h-7 w-7 text-primary" />
                </div>
              </div>
              <div className="space-y-2.5">
                <h3 className="text-2xl font-semibold tracking-tight text-foreground">
                  {folderData ? `Mappen "${folderData.name}" är tom` : "Kom igång"}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground max-w-sm mx-auto">
                  {folderData
                    ? "Skapa underlag för Bas-P eller dokument för Bas-U"
                    : "Skapa direkt i projektet – mappar är helt valfria och kan läggas till senare."
                  }
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="relative space-y-8">
              {canCreateContent && (
                <>
                  <ActionGroup
                    label="Bas-P"
                    description="Projektering och underlag"
                    tone="basp"
                    columns={3}
                  >
                    <CardActionItem
                      icon={FileText}
                      title="Risksammanställning"
                      description="Starta en ny riskbedömning för projektet"
                      onClick={handleCreateRiskAssessment}
                    />

                    <CardActionItem
                      icon={BarChart3}
                      title="SKR-bedömning"
                      description="Systematisk riskanalys av storlek, komplexitet och risk"
                      onClick={handleCreateSKRAssessment}
                    />

                    <CardActionItem
                      icon={ShieldCheck}
                      title="Arbetsområdesutredning"
                      description="Utred arbetsområdet enligt dok 10"
                      onClick={handleCreateWorkArea}
                    />
                  </ActionGroup>

                  <div className="h-px w-full bg-border/70" />

                  <ActionGroup
                    label="Bas-U"
                    description="Utförande på arbetsplatsen"
                    tone="basu"
                    columns={3}
                  >
                    <CardActionItem
                      icon={HardHat}
                      title="Arbetsberedning"
                      description="Utgå från en risksammanställning eller börja från början"
                      onClick={handleCreateWorkPreparation}
                    />

                    <CardActionItem
                      icon={CalendarRange}
                      title="Skedesplan / APD-plan"
                      description="Dok 26 · Skeden, samtidiga arbeten och gemensamma skydd"
                      onClick={() => openSamordning("skedesplan")}
                    />

                  </ActionGroup>

                  <div className="h-px w-full bg-border/70" />

                  <ActionGroup
                    label="Gemensamt"
                    description="När projektering och produktion pågår parallellt"
                    tone="neutral"
                    columns={3}
                  >
                    <CardActionItem
                      icon={Users}
                      title="Samordningsmöte Bas-P / Bas-U"
                      description="Dok 23 · Protokoll över risker, ändringar och beslut"
                      onClick={() => openSamordning("samordning")}
                    />
                  </ActionGroup>



                  <div className="h-px w-full bg-border/70" />
                </>
              )}

              <ActionGroup
                label="Organisera"
                description="Valfritt – strukturera innehållet i mappar"
                tone="neutral"
                columns={3}
              >
                <CardActionItem
                  icon={FolderPlus}
                  title={folderData ? "Undermapp" : "Ny mapp"}
                  description="Valfritt – gruppera innehållet i mappar"
                  onClick={() => setIsCreateFolderDialogOpen(true)}
                  actionLabel="Skapa mapp"
                />
              </ActionGroup>
            </div>
          </CardContent>
        </Card>
      </div>


      {activeFolderId && (
        <CreateAssessmentDialog
          isOpen={isCreateAssessmentDialogOpen}
          onOpenChange={setIsCreateAssessmentDialogOpen}
          projectId={projectId}
          folderId={activeFolderId}
          onSuccess={handleCreateAssessmentSuccess}
          isSharedFolder={isShared}
        />
      )}

      <CreateFolderDialog
        isOpen={isCreateFolderDialogOpen}
        onOpenChange={setIsCreateFolderDialogOpen}
        onSubmit={handleCreateFolderSubmit}
        folderName={folderName}
        setFolderName={setFolderName}
      />
    </>
  );
};
